import { uniqBy } from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Status } from 'app-types';

export const FILTER_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 500;

export interface PageResult<T> {
	items: T[];
	hasMore: boolean;
}

type PageFetcher<T> = (
	tenantId: number | undefined,
	page: number,
	limit: number,
	search: string
) => Promise<PageResult<T>>;

export class PagedList<T extends { id: number | string }> {
	items: T[] = [];
	hasMore = false;
	status = Status.Idle;
	loadMoreStatus = Status.Idle;
	loadAllStatus = Status.Idle;
	tenantId: number | undefined = undefined;
	search = '';
	private nextPage = 1;
	private requestId = 0;
	private loadAllPromise: Promise<T[] | undefined> | null = null;

	constructor(private fetchPage: PageFetcher<T>) {
		makeObservable(this, {
			items: observable,
			hasMore: observable,
			status: observable,
			loadMoreStatus: observable,
			loadAllStatus: observable,
			load: action,
			loadMore: action,
			loadAll: action
		});
	}

	async load(tenantId?: number, search = ''): Promise<void> {
		const requestId = (this.requestId += 1);
		this.loadAllPromise = null;
		runInAction(() => {
			this.items = [];
			this.hasMore = false;
			this.tenantId = tenantId;
			this.search = search;
			this.nextPage = 1;
			this.status = Status.Pending;
			this.loadMoreStatus = Status.Idle;
			this.loadAllStatus = Status.Idle;
		});
		try {
			const result = await this.fetchPage(
				tenantId,
				1,
				FILTER_PAGE_SIZE,
				search
			);
			if (requestId !== this.requestId) return;
			runInAction(() => {
				this.items = uniqBy(result.items, 'id');
				this.hasMore = result.hasMore;
				this.nextPage = 2;
				this.status = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.requestId) return;
			runInAction(() => {
				this.status = Status.Failure;
			});
			throw e;
		}
	}

	async loadMore(): Promise<void> {
		if (
			!this.hasMore ||
			this.status !== Status.Success ||
			this.loadMoreStatus === Status.Pending ||
			this.loadAllPromise
		) {
			return;
		}
		const requestId = this.requestId;
		const page = this.nextPage;
		runInAction(() => {
			this.loadMoreStatus = Status.Pending;
		});
		try {
			const result = await this.fetchPage(
				this.tenantId,
				page,
				FILTER_PAGE_SIZE,
				this.search
			);
			if (requestId !== this.requestId) return;
			runInAction(() => {
				this.items = uniqBy(this.items.concat(result.items), 'id');
				this.hasMore =
					this.hasMore && result.hasMore && result.items.length > 0;
				this.nextPage = page + 1;
				this.loadMoreStatus = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.requestId) return;
			runInAction(() => {
				this.loadMoreStatus = Status.Failure;
			});
			throw e;
		}
	}

	loadAll(): Promise<T[] | undefined> {
		if (this.status === Status.Success && !this.hasMore) {
			return Promise.resolve(this.items);
		}
		if (this.loadAllPromise) return this.loadAllPromise;
		const load = this.fetchAllPages(this.requestId);
		const clear = () => {
			if (this.loadAllPromise === load) this.loadAllPromise = null;
		};
		this.loadAllPromise = load;
		load.then(clear, clear);
		return load;
	}

	private async fetchAllPages(requestId: number): Promise<T[] | undefined> {
		runInAction(() => {
			this.loadAllStatus = Status.Pending;
		});
		try {
			const loaded: T[] = [];
			for (let page = 1; ; page += 1) {
				const result = await this.fetchPage(
					this.tenantId,
					page,
					MAX_PAGE_SIZE,
					this.search
				);
				if (requestId !== this.requestId) return undefined;
				loaded.push(...result.items);
				if (!result.hasMore || !result.items.length) break;
			}
			runInAction(() => {
				this.items = uniqBy(this.items.concat(loaded), 'id');
				this.hasMore = false;
				this.status = Status.Success;
				this.loadAllStatus = Status.Success;
			});
			return this.items;
		} catch (e) {
			if (requestId !== this.requestId) return undefined;
			runInAction(() => {
				this.loadAllStatus = Status.Failure;
			});
			throw e;
		}
	}
}
